<template>
  <div
    class="product__add-to-cart gap-4"
    :class="{
      'flex flex-col items-start': hasQuantitySelector
    }"
  >
    <link
      :itemprop="SCHEMA_ITEM_PROP.AVAILABILITY"
      :href="schemaAvailabilityLink"
    >
    <div v-if="isQuantitySelectionPossible && hasQuantitySelector">
      <QuantitySelector
        :qty="initialQuantityToAdd"
        :stock="availableQuantity"
        :has-controls="true"
        :align-left="true"
        :is-debounced="false"
        :sku="productSku"
        :triggered-in="EVENT_SOURCE.PDP"
        :name="`${productSku}-add-to-cart-quantity-selector`"
        class="w-44"
        @input="quantityChanged"
      />
    </div>
    <CustomButton
      v-if="availableQuantity > 0"
      :key="availableQuantity"
      type="primary"
      :data-testid="isMounted && 'add-to-cart__button'"
      :text="$t('Add to cart') "
      :disabled="!isQuantitySelectionPossible || checkoutIsDisabled"
      class="w-full"
      @click="onAddQuantity"
    />
    <NotifyMe
      v-else
      :product-sku="productSku"
    />
    <CustomButton
      v-if="canShowBuyNow"
      :text="$t('Buy now')"
      type="secondary"
      :data-testid="isMounted && 'buy-now__button'"
      size="md"
      class="w-full"
      :disabled="checkoutIsDisabled"
      @click="onBuyNow"
    />
    <CustomButton
      v-if="canShowExploreAlternatives"
      :text="$t('Explore alternative products')"
      type="primary"
      :data-testid="isMounted && 'explore-alternatives__button'"
      data-gtm="explore-alternatives__button"
      @click="onExploreAlternatives"
    />
  </div>
</template>

<script lang="ts">

import type { ProductVariant } from '~~/types/commercetools';
import { productGetters } from '~/utils/ct';

import { SCHEMA_ITEM_PROP, SCHEMA_ITEM_TYPE } from '~~/constants/seo';
import { isTestProduct } from '~~/helpers/product/isTestProduct';
import { EVENT_SOURCE } from '~~/constants/googleTagManager';
import { ROUTES } from '~~/constants/routes';
import { isCheckoutDisabled } from '~~/helpers/Checkout/isCheckoutDisabled';
import useAlternativeProductsModal from '~/composables/stores/useAlternativeProductsModalStore';

export default defineComponent({
  name: 'AddToCart',

  props: {
    product: {
      type: Object as PropType<ProductVariant>,
      required: true,
    },
    productAdjustedForCart: {
      type: Object as PropType<ProductVariant>,
      default: () => {},
    },
    hasQuantitySelector: {
      type: Boolean,
      default: true,
    },
    predefinedQuantity: {
      type: Number,
      default: null,
    },
    isFloatingAddToCart: {
      type: Boolean,
      default: false,
    },
    hasInStockAlternativeProducts: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const { product } = toRefs(props);
    const route = useRoute();

    const { slug } = route.params;
    const initialQuantityToAdd = ref(1);

    const { getAdjustedSlug } = useRouteExtended();
    const router = useRouter();
    const { countryCode } = useLocalization();
    const { openAlternativeProductsModal } = useAlternativeProductsModal();

    const quantityToAdd: ComputedRef<number> = computed(
      () => props.predefinedQuantity || initialQuantityToAdd.value,
    );

    const productSku = productGetters.getSku(props.product);
    const productName = productGetters.getName(props.product);
    const productImage = productGetters.getCoverImage(props.product);
    const { getPriceInformation } = useProductPrice();
    const { fullProductPrice } = getPriceInformation(product);
    const {
      availableQuantity,
      isQuantitySelectionPossible,
      addToCartButtonText,
    } = useAddToCart(slug, productSku);

    const { addQuantity } = useAddToCartQuantity({
      sku: productSku,
      slug,
      fullProductPrice: fullProductPrice.value,
      productName,
      productImage,
      isMainProduct: true,
      addToCartText: addToCartButtonText.value,
    });

    const onAddQuantity = () => {

      addQuantity(quantityToAdd.value);
      resetQuantity();
    
    };

    const onBuyNow = async () => {

      await addQuantity(quantityToAdd.value, { shouldOpenCart: true });
      resetQuantity();
      router.push(getAdjustedSlug(ROUTES.CHECKOUT_ADDRESSES));
    
    };

    const canShowBuyNow = computed(() => {

      return !props.isFloatingAddToCart && isQuantitySelectionPossible.value;
    
    });

    const onExploreAlternatives = () => {

      openAlternativeProductsModal();
    
    };

    const canShowExploreAlternatives = computed(() => {

      return props.hasInStockAlternativeProducts && !props.isFloatingAddToCart && !isQuantitySelectionPossible.value;
    
    });

    const { isMounted } = useIsMounted();

    const resetQuantity = () => {

      initialQuantityToAdd.value = 1;
    
    };

    const quantityChanged = (value: number) => {

      initialQuantityToAdd.value = value;
    
    };

    const schemaAvailabilityLink = computed(() => {

      if (isTestProduct(productSku)) {

        return SCHEMA_ITEM_TYPE.OFFER_PRE_ORDER;
      
      } else if (isQuantitySelectionPossible.value) {

        return SCHEMA_ITEM_TYPE.OFFER_IN_STOCK;
      
      } else {

        return SCHEMA_ITEM_TYPE.OFFER_OUT_OF_STOCK;
      
      }
    
    });

    const checkoutIsDisabled = isCheckoutDisabled(countryCode.value);

    return {
      isMounted,
      initialQuantityToAdd,
      availableQuantity,
      onAddQuantity,
      isQuantitySelectionPossible,
      addToCartButtonText,
      SCHEMA_ITEM_PROP,
      SCHEMA_ITEM_TYPE,
      quantityChanged,
      schemaAvailabilityLink,
      productSku,
      EVENT_SOURCE,
      onBuyNow,
      canShowBuyNow,
      onExploreAlternatives,
      canShowExploreAlternatives,
      checkoutIsDisabled,
    };
  
  },
});


</script>
